/**
 * Bonsai OS Data Saver (extends OS Lite).
 * Same localStorage key as Lite so existing preferences survive the rename.
 * Guests may also receive `dataSaver` on handshake / OS_DATA_SAVER messages.
 */

export const OS_DATA_SAVER_STORAGE_KEY = "bonsai_os_lite_mode_v1";
/** DOM CustomEvent — same wire as historical Lite mode. */
export const OS_DATA_SAVER_EVENT = "bonsai-os:lite-mode";
/** Host → guest postMessage when preference changes without full remount. */
export const OS_DATA_SAVER = "bonsai-os:data-saver";

/** One-time Hub banner dismiss key (browser saveData suggestion). */
export const OS_DATA_SAVER_BANNER_DISMISS_KEY = "bonsai_os_data_saver_banner_v1";

export type OsDataSaverDetail = { enabled: boolean };

export type OsDataSaverMessage = {
  type: typeof OS_DATA_SAVER;
  dataSaver: boolean;
};

function readQueryDataSaver(): boolean | null {
  if (typeof location === "undefined") return null;
  try {
    const q = new URLSearchParams(location.search);
    const v = q.get("lite") ?? q.get("dataSaver") ?? q.get("datasaver");
    if (v === "1" || v === "true" || v === "on") return true;
    if (v === "0" || v === "false" || v === "off") return false;
  } catch {
    /* ignore */
  }
  return null;
}

/** True when the user opted into Data Saver / Lite (or `?lite=1`). */
export function isOsDataSaver(): boolean {
  const fromQuery = readQueryDataSaver();
  if (fromQuery != null) return fromQuery;
  try {
    return localStorage.getItem(OS_DATA_SAVER_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Alias — Lite and Data Saver share storage. */
export const isOsLiteMode = isOsDataSaver;

export function applyOsDataSaverClass(enabled = isOsDataSaver()): void {
  if (typeof document === "undefined") return;
  try {
    document.documentElement.classList.toggle("landing-lite", enabled);
    document.documentElement.classList.toggle("os-data-saver", enabled);
  } catch {
    /* ignore */
  }
}

export function setOsDataSaver(
  enabled: boolean,
  opts?: { reload?: boolean; atmosphereFxKey?: string },
): void {
  const fxKey = opts?.atmosphereFxKey ?? "bonsai_os_atmosphere_fx_v1";
  try {
    localStorage.setItem(OS_DATA_SAVER_STORAGE_KEY, enabled ? "1" : "0");
    if (enabled) {
      localStorage.setItem(fxKey, "0");
    } else {
      const fx = localStorage.getItem(fxKey);
      if (fx === "0") localStorage.removeItem(fxKey);
    }
  } catch {
    /* ignore */
  }
  applyOsDataSaverClass(enabled);
  try {
    window.dispatchEvent(
      new CustomEvent<OsDataSaverDetail>(OS_DATA_SAVER_EVENT, {
        detail: { enabled },
      }),
    );
  } catch {
    /* ignore */
  }
  if (opts?.reload === false) return;
  if (typeof location === "undefined") return;
  try {
    const u = new URL(location.href);
    let dirty = false;
    for (const key of ["lite", "dataSaver", "datasaver"]) {
      if (u.searchParams.has(key)) {
        u.searchParams.delete(key);
        dirty = true;
      }
    }
    if (dirty) {
      location.replace(u.href);
      return;
    }
  } catch {
    /* ignore */
  }
  location.reload();
}

/** Best-effort: browser Save-Data header / Network Information API. */
export function browserRequestsSaveData(): boolean {
  try {
    const conn = (
      navigator as Navigator & {
        connection?: { saveData?: boolean };
      }
    ).connection;
    return Boolean(conn?.saveData);
  } catch {
    return false;
  }
}

export function isDataSaverBannerDismissed(): boolean {
  try {
    return localStorage.getItem(OS_DATA_SAVER_BANNER_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissDataSaverBanner(): void {
  try {
    localStorage.setItem(OS_DATA_SAVER_BANNER_DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function postOsDataSaver(
  target: Window,
  origin: string,
  dataSaver: boolean,
): void {
  target.postMessage(
    { type: OS_DATA_SAVER, dataSaver } satisfies OsDataSaverMessage,
    origin,
  );
}
